import React from 'react';
import { useLanguage } from '../../context/LanguageContext';

const sanitizeKey = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

/**
 * DynamicQuestions component for rendering country-specific questions
 * @param {Object} props - Component props
 * @param {Array} props.questions - Questions array
 * @param {Object} props.answers - Current answers object
 * @param {Function} props.onAnswer - Callback for answer changes
 */
const DynamicQuestions = ({ questions = [], answers = {}, onAnswer }) => {
  const { t } = useLanguage();

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('wizard.dynamic.title', 'Additional Questions')}
        </h3>
        <p className="text-gray-600">
          {t('wizard.dynamic.subtitle', 'These questions help us provide more personalized guidance for your immigration journey.')}
        </p>
      </div>

      {questions.map((question, index) => (
        <QuestionCard
          key={question.id}
          question={question}
          index={index}
          value={answers[question.id] || ''}
          onChange={(value) => onAnswer(question.id, value)}
        />
      ))}
    </div>
  );
};

/**
 * QuestionCard component for individual question rendering
 */
const QuestionCard = ({ question, index, value, onChange }) => {
  const { t } = useLanguage();
  const handleOptionChange = (selectedOption) => {
    onChange(selectedOption);
  };

  return (
    <div className="card p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-medium text-sm">
          {index + 1}
        </div>

        <div className="flex-1">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            {t(`wizard.dynamic.questions.${question.id}.question`, question.question)}
          </h4>

          {question.type === 'radio' && (
            <RadioGroup
              options={question.options}
              name={question.id}
              value={value}
              onChange={handleOptionChange}
              ariaLabel={t('wizard.dynamic.radioGroupLabel', 'Answer choices')}
              translationPrefix={`wizard.dynamic.questions.${question.id}.options`}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * RadioGroup component for radio button selection
 */
const RadioGroup = ({ options = [], name, value, onChange, ariaLabel, translationPrefix }) => {
  const { t } = useLanguage();

  const handleOptionChange = (option) => {
    onChange(option);
  };

  return (
    <div className="space-y-3" role="radiogroup" aria-label={ariaLabel}>
      {options.map((option, index) => (
        <label
          key={index}
          className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
            value === option
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option}
            onChange={() => handleOptionChange(option)}
            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
          />
          <span className={`ml-3 font-medium ${
            value === option ? 'text-blue-900' : 'text-gray-700'
          }`}>
            {translationPrefix
              ? t(`${translationPrefix}.${sanitizeKey(option)}`, option)
              : option}
          </span>
        </label>
      ))}
    </div>
  );
};

export default DynamicQuestions;
